import { useI18N } from "@sundae/react-hooks";
import { Text } from "@sundae/ui-toolkit";
import { TCIP30ErrorWithCode } from "@sundae/wallets/dist/types/cip30.types";
import type {
  DepositConfigArgs,
  FreezerConfigArgs,
  SwapConfigArgs,
  WithdrawConfigArgs,
  ZapConfigArgs,
} from "@sundaeswap/core";
import type { IDepositArgs, IWithdrawArgs } from "@sundaeswap/taste-test";
import { useCallback, useState } from "react";

import { ITransactionErrorActions } from "../stores/ExchangeContext";
import { EOrderFlowState, OrderActions, useOrderContext } from "../stores/OrderContext";
import { TransactionErrorHandler } from "../utils/transactions/TransactionErrorHandler.class";

export enum ETransactionErrorState {
  BUILD = "BUILD",
  SIGN = "SIGN",
  SEND = "SEND",
}

export enum ETransactionErrorMessages {
  INPUTS_EXHAUSTED = "InputsExhaustedError",
  NOT_ENOUGH_ADA = "/var/task/transaction-writer transaction-json failed: transaction-writer: not enough Ada:",
  MISSING_INPUT_OUTPUT = "Missing input or output for some native asset",
  NO_COLLATERAL_UTXOS = "No collateral UTXOs found.",
  TOO_MANY_INPUTS = "Too many inputs used.",
  FEE_EXCEEDS_MAXIMUM = "The fee selected for this transaction exceeds the maximum that we allow. Please try again.",
  NO_FACTORY = "No factory data found.",
}

export interface ITransactionErrorShape {
  error: TCIP30ErrorWithCode | Error | string;
  transactionState: ETransactionErrorState;
  cbor?: string;
}

export type TAllConfigTypes =
  | ZapConfigArgs
  | DepositConfigArgs
  | SwapConfigArgs
  | WithdrawConfigArgs
  | FreezerConfigArgs
  | Pick<FreezerConfigArgs, "ownerAddress" | "existingPositions">
  | IDepositArgs
  | IWithdrawArgs;

/**
 * Handles transaction errors in transaction flows.
 * @param customStateFn @TODO Remove this once transaction states are unified.
 * @returns
 */
export const useTransactionErrorHandler = (customStateFn?: () => void) => {
  const { t } = useI18N("transactions");
  const { dispatch } = useOrderContext();
  const [handler, setHandler] = useState<TransactionErrorHandler>();

  const handleTransactionError = useCallback(
    (
      error: TCIP30ErrorWithCode | Error | string,
      actions: ITransactionErrorActions,
      transactionState: ETransactionErrorState,
      config?: TAllConfigTypes,
      cbor?: string
    ) => {
      const handler = new TransactionErrorHandler({
        error,
        transactionState,
        cbor,
        config,
      });
      if (!handler.isKnownError()) {
        OrderActions.setTransaction(
          {
            error: {
              actions,
              handler,
              message: (
                <>
                  <Text tag="p" className="mt-4">
                    {t(`errors.generic.resolution`, {
                      // @TODO: auto report errors that don't cause a fallback
                      // context: errorReportingEnabled ? "reporting" : "no_reporting",
                      context: "no_reporting",
                    })}
                  </Text>
                </>
              ),
            },
          },
          dispatch,
          "useTransactionErrorHandler.handleTransactionErrorCallback.unknownError"
        );
      } else {
        setHandler(handler);
        OrderActions.setTransaction(
          {
            error: {
              actions,
              handler,
              message: (
                <>
                  <Text tag="p" className="mb-0" size="sm">
                    <Text tag="span" weight="bold" className="block w-full" size="base">
                      {t("errors.generic.prefix", { context: "error" })}
                    </Text>
                    <Text tag="span" size="sm" variant="muted">
                      {t(`errors.${transactionState}.${handler.getCode()}.error`)}
                    </Text>
                  </Text>
                  <Text tag="p" className="mt-4" size="sm">
                    <Text tag="span" weight="bold" className="block w-full" size="base">
                      {t("errors.generic.prefix", { context: "resolution" })}
                    </Text>
                    <Text tag="span" size="sm" variant="muted">
                      {t(`errors.${transactionState}.${handler.getCode()}.resolution`)}
                    </Text>
                  </Text>
                </>
              ),
            },
          },
          dispatch,
          "useTransactionErrorHandler.handleTransactionErrorCallback.knownError"
        );
      }
      customStateFn
        ? customStateFn()
        : OrderActions.setOrderFlowState(
            EOrderFlowState.error,
            dispatch,
            "useTransactionErrorHandler.customStateFnFallback"
          );
    },
    []
  );

  const clearTransactionError = useCallback((flowState?: EOrderFlowState) => {
    setHandler(undefined);
    OrderActions.setTransaction(
      { error: undefined },
      dispatch,
      "useTransactionErrorHandler.clearTransactionErrorCallback"
    );
    flowState &&
      OrderActions.setOrderFlowState(
        flowState,
        dispatch,
        "useTransactionErrorHandler.clearTransactionErrorCallback"
      );
  }, []);

  return {
    errorHandler: handler,
    clearTransactionError,
    handleTransactionError,
  };
};
